import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import NavBar from "./components/Navbar";
import ProfileMenu from "./components/ProfileMenu";
import PendingTradeAlert from "./components/PendingTradeAlert";
import SwimmerTracker from "./components/SwimmerTracker";
import Login from "./components/Login";
import HomeDashboard from "./components/HomeDashboard";
import SwimmersView from "./components/SwimmersView";
import DraftBoard from "./components/DraftBoard";
import TradingView from "./components/TradingView";
import AdminDashboard from "./components/AdminDashboard";
import TeamView from "./components/TeamView";
import LeagueView from "./components/LeagueView";
import { getAccessToken, logoutUser, restoreSessionUser } from "./utils/auth";
import { API_BASE_URL } from "./utils/api";

const API = API_BASE_URL;

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [sessionError, setSessionError] = useState("");
  const [profileOpenRequestToken, setProfileOpenRequestToken] = useState(0);
  const [tradeAlertCheckToken, setTradeAlertCheckToken] = useState(0);
  const [pendingTradeCount, setPendingTradeCount] = useState(0);
  const [showPendingTradeAlert, setShowPendingTradeAlert] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const bootstrapSession = async () => {
      const restored = await restoreSessionUser();
      if (isMounted && restored.user) {
        setCurrentUser(restored.user);
      }
      if (isMounted && restored.error) {
        setSessionError(restored.error);
      }
      if (isMounted) {
        setCheckingSession(false);
      }
    };

    bootstrapSession();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleAuthSuccess = (user) => {
    setSessionError("");
    setCurrentUser(user);
    setTradeAlertCheckToken((prev) => prev + 1);
  };

  const handleLogout = async () => {
    await logoutUser();
    setCurrentUser(null);
    setPendingTradeCount(0);
    setShowPendingTradeAlert(false);
  };

  const handleUserUpdate = (updatedUser) => {
    setCurrentUser((prev) => ({ ...(prev || {}), ...(updatedUser || {}) }));
  };

  const handleOpenProfile = () => {
    setProfileOpenRequestToken((prev) => prev + 1);
  };

  useEffect(() => {
    let isMounted = true;

    const checkPendingTradesAfterLogin = async () => {
      if (!currentUser || tradeAlertCheckToken === 0) {
        return;
      }

      const token = getAccessToken();
      if (!token) {
        return;
      }

      try {
        const res = await axios.get(`${API}/trades/state`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const proposals = Array.isArray(res.data?.trade_proposals)
          ? res.data.trade_proposals
          : [];
        const myCoach = res.data?.my_coach;
        const incomingPendingCount = proposals.filter(
          (proposal) =>
            proposal?.to_coach === myCoach &&
            proposal?.status === "pending_coach",
        ).length;

        if (!isMounted) return;

        setPendingTradeCount(incomingPendingCount);
        setShowPendingTradeAlert(incomingPendingCount > 0);
      } catch {
        if (!isMounted) return;
        setPendingTradeCount(0);
        setShowPendingTradeAlert(false);
      }
    };

    checkPendingTradesAfterLogin();

    return () => {
      isMounted = false;
    };
  }, [currentUser, tradeAlertCheckToken]);

  if (checkingSession) {
    return <div style={styles.container}>Checking session...</div>;
  }

  if (!currentUser) {
    return (
      <div style={styles.container}>
        <Login onAuthSuccess={handleAuthSuccess} initialError={sessionError} />
      </div>
    );
  }

  return (
    <Router>
      <div style={styles.container} className="app-root">
        <PendingTradeAlert
          isOpen={showPendingTradeAlert}
          pendingCount={pendingTradeCount}
          onClose={() => setShowPendingTradeAlert(false)}
        />
        <ProfileMenu
          user={currentUser}
          onUserUpdate={handleUserUpdate}
          openRequestToken={profileOpenRequestToken}
        />
        <NavBar
          user={currentUser}
          onLogout={handleLogout}
          onOpenProfile={handleOpenProfile}
        />
        <main className="app-main-content">
          <Routes>
            <Route
              path="/"
              element={
                <HomeDashboard coachName={currentUser?.coachName || "Coach"} />
              }
            />
            <Route path="/draft" element={<DraftBoard user={currentUser} />} />
            <Route path="/team" element={<TeamView user={currentUser} />} />
            <Route
              path="/team/trading"
              element={<TeamView user={currentUser} />}
            />
            <Route path="/swimmers" element={<SwimmersView />} />
            <Route
              path="/swimmers/leaders"
              element={<Navigate to="/swimmers" replace />}
            />
            <Route path="/league" element={<LeagueView />} />
            <Route path="/league/bracket" element={<LeagueView />} />
            <Route path="/league/teams" element={<LeagueView />} />
            <Route path="/league/rules" element={<LeagueView />} />
            <Route path="/tracker/:swimmerId" element={<SwimmerTracker />} />
            <Route
              path="/trading"
              element={<TradingView user={currentUser} />}
            />
            {currentUser.isAdmin && (
              <Route path="/admin" element={<AdminDashboard />} />
            )}

            {/* Legacy aliases */}
            <Route
              path="/tracker"
              element={<Navigate to="/swimmers" replace />}
            />
            <Route path="/coach" element={<Navigate to="/league" replace />} />
            <Route
              path="/bracket"
              element={<Navigate to="/league/bracket" replace />}
            />
            <Route
              path="/rules"
              element={<Navigate to="/league/rules" replace />}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    fontFamily: "Inter, Segoe UI, Arial, sans-serif",
    background: "#02061a",
    color: "#e8f0ff",
  },
};

export default App;
